import Navbar from 'src/components/HorizontalLayout/Navbar';
import React, { useState, useEffect } from "react";
import axiosInstance from 'src/common/axiosInstance';
import { toast } from 'react-toastify';

import {
    Card,
    CardBody,
    Container,
    Modal,
    ModalFooter,
    ModalBody,
    ModalHeader,
    Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import "./dataOptions.css"

//Import Icons
import { DataOptionsIcon } from 'src/components/Icons/DataOptionsIcon';
import { UnifiedIcon } from 'src/components/Icons/UnifiedIcon';

const DataOptions = () => {

    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasRequested, setHasRequested] = useState(false);
    const [requestedAt, setRequestedAt] = useState<string | null>(null);
    const [checkingRequest, setCheckingRequest] = useState(true);
    const [isCreated, setIsCreated] = useState(false);
    const [userEmail, setUserEmail] = useState<string>('');
    const [isNewRequest, setIsNewRequest] = useState(false);
    const [agencyName, setAgencyName] = useState<string>('');
    const [contactName, setContactName] = useState<string>('');

    const toggleConfirmationModal = () => {
        setConfirmationModalOpen(!confirmationModalOpen)
        // Reset isNewRequest when closing modal
        if (confirmationModalOpen) {
            setIsNewRequest(false);
        }
    }

    // Check if user has already requested on component mount
    useEffect(() => {
        checkUserRequest();
    }, []);

    const checkUserRequest = async () => {
        try {
            setCheckingRequest(true);

            // Get data from localStorage
            const authUserStr = localStorage.getItem('authUser');
            const agencyInfoStr = localStorage.getItem('agencyInfo');

            if (!authUserStr || !agencyInfoStr) {
                return;
            }

            const authUser = JSON.parse(authUserStr);
            const agencyInfo = JSON.parse(agencyInfoStr);

            const userId = authUser.user?.users_id;
            const sfId = agencyInfo.data?.sf_id;
            const email = authUser.user?.email;
            const agencyName = agencyInfo.data?.agency_name || agencyInfo.data?.name || 'your agency';

            if (!userId || !sfId) {
                return;
            }

            // Store user email and agency name for later use
            if (email) {
                setUserEmail(email);
            }
            if (agencyName) {
                setAgencyName(agencyName);
            }

            const payload = {
                user_id: userId,
                sf_id: sfId
            };

            const response = await axiosInstance.post('/check-user-request', payload);

            // Check if credentials are already created (either via already_created flag or has_requested with is_created)
            if (response.data.status === 'success' && response.data.already_created) {
                setContactName(response.data.data.name);
                setIsCreated(true);
                setHasRequested(true);
                setRequestedAt(response.data.data.requested_at || new Date().toISOString());
                // Automatically show modal if credentials are already created
                setConfirmationModalOpen(true);
            } else if (response.data.status === 'success' && response.data.has_requested) {
                const requestData = response.data.data;
                const credentialsCreated = requestData.is_created === 1 || requestData.is_created === true;

                //check if credentials are created (requested + created)
                if (credentialsCreated) {
                    setIsCreated(true);
                    setHasRequested(true);
                    setRequestedAt(requestData.requested_at);
                    // Automatically show modal if credentials are already created
                    setConfirmationModalOpen(true);
                } else {
                    // Only requested, not created yet
                    setHasRequested(true);
                    setRequestedAt(requestData.requested_at);
                    setIsCreated(false);
                }
            }

        } catch (error: any) {
            console.error('Error checking user request:', error);
        } finally {
            setCheckingRequest(false);
        }
    }

    const getTimeDifferenceInMinutes = (requestedAt: string) => {
        const requestedTime = new Date(requestedAt);
        const currentTime = new Date();
        const diffInMs = currentTime.getTime() - requestedTime.getTime();
        return Math.floor(diffInMs / (1000 * 60));
    }

    const getModalMessage = () => {
        if (!requestedAt) return '';

        // If credentials are already created
        if (isCreated && contactName !== '') {
            const contact = contactName || 'support';
            return `Snowflake credentials already created for ${agencyName}, please contact ${contact} for credentials.`;
        }

        const minutesPassed = getTimeDifferenceInMinutes(requestedAt);

        if (minutesPassed < 10) {
            return `Your request was already made at ${new Date(requestedAt).toLocaleString()}. You will receive an email with credentials within 10 minutes.`;
        } else {
            return `Your request was already made at ${new Date(requestedAt).toLocaleString()}. Please check your email for credentials. If not received, reach out to support.`;
        }
    }

    const handleRequestCredentials = async () => {
        try {
            setLoading(true);

            // Get data from localStorage
            const authUserStr = localStorage.getItem('authUser');
            const agencyInfoStr = localStorage.getItem('agencyInfo');

            if (!authUserStr || !agencyInfoStr) {
                toast.error('Unable to retrieve user information. Please login again.');
                setLoading(false);
                return;
            }

            const authUser = JSON.parse(authUserStr);
            const agencyInfo = JSON.parse(agencyInfoStr);

            const userId = authUser.user?.users_id;
            const sfId = agencyInfo.data?.sf_id;

            if (!userId || !sfId) {
                toast.error('Required information is missing. Please contact support.');
                setLoading(false);
                return;
            }

            // First, check the current status from the backend
            const checkPayload = {
                user_id: userId,
                sf_id: sfId
            };

            const checkResponse = await axiosInstance.post('/check-user-request', checkPayload);

            if (checkResponse.data.status === 'success' && checkResponse.data.has_requested) {
                const requestData = checkResponse.data.data;
                const credentialsCreated = requestData.is_created === 1 || requestData.is_created === true;

                // Update state with latest data from backend
                setHasRequested(true);
                setRequestedAt(requestData.requested_at);
                setIsCreated(credentialsCreated);
                setIsNewRequest(false); // Not a new request

                setLoading(false);
                setConfirmationModalOpen(true);
                return;
            }

            // If no existing request, proceed to create a new one
            const userName = authUser.user?.name;
            const userEmail = authUser.user?.email;

            if (!userEmail) {
                toast.error('Email is required. Please contact support.');
                setLoading(false);
                return;
            }

            // Prepare payload
            const payload = {
                user_id: userId,
                name: userName,
                email: userEmail,
                sf_id: sfId
            };

            // Request new credentials
            const response = await axiosInstance.post('/request-snowflake-credentials', payload);

            if (response.data.status === 'success') {
                setHasRequested(true);
                setRequestedAt(response.data.data.requested_at);
                setIsCreated(false); // New request, so not created yet
                setIsNewRequest(true); // Mark as new request
                setConfirmationModalOpen(true);
            } else if (response.data.status === 'info') {
                // Already requested
                setHasRequested(true);
                setRequestedAt(response.data.data.requested_at);
                setIsNewRequest(false); // Not a new request
                setConfirmationModalOpen(true);
            } else {
                toast.error(response.data.message || 'Failed to request credentials');
            }

        } catch (error: any) {
            console.error('Error requesting Snowflake credentials:', error);
            toast.error(error.response?.data?.message || 'Failed to request credentials. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <React.Fragment>
            <div className="page-content page-content-data-options">
                <Navbar />
                <Container fluid className="data-options-container">
                    <Breadcrumbs title="Setting" breadcrumbItem="Data Options" titleLink="/branding" showLeftTitle={false}/>
                    <Card className="mb-3">
                        <CardBody>
                            <div className='data-options-header'>
                                <h2>
                                    Welcome to Your Data Options
                                </h2>
                                <p>At Unified Data Studio, we understand that every agency has its own way of turning data into insights.
                                    That&apos;s why we offer you two flexible paths to get the most out of your advertising data.</p>
                            </div>
                        </CardBody>
                    </Card>
                    <div className='data-options-section'>
                        <Card>
                            <CardBody>
                                <div className='section-header'>
                                    <div className="title-section">
                                          <UnifiedIcon className='icon-color' />
                                        <h5>
                                            Stay with Our Unified Dashboards
                                        </h5>
                                    </div>
                                    <span
                                        className={`data-options-badge badge font-size-16 badge-success-subtle }`}
                                    >
                                        Currently Active
                                    </span>
                                </div>
                                <p>Experience our beautifully crafted ready-to-use dashboards with integrated
                                    reporting across all advertising exchanges.</p>
                                <ul className='features-list'>
                                    <li><span className='list-color'></span>Zero setup required</li>
                                    <li><span className='list-color'></span>Cross-platform reporting</li>
                                    <li><span className='list-color'></span>Real time visualisation</li>
                                </ul>
                                <span className="btn-disabled">
                                    <Button
                                        color="primary"
                                        type="submit"
                                        className="btn btn-primary button-primary"
                                        disabled
                                    >Use Unified Data Studio</Button>
                                </span>
                            </CardBody>
                        </Card>
                        <Card>
                            <CardBody>
                                <div className='section-header'>
                                    <div className='title-section'>
                                           <DataOptionsIcon className="icon-color" />
                                        <h5>
                                            Connect Your Own BI Tools via Snowflake
                                        </h5>
                                    </div>
                                    {hasRequested && (
                                        <span className={`data-options-badge badge font-size-16 ${isCreated ? 'badge-success-subtle' : 'badge-warning-subtle'}`}>
                                            {isCreated ? 'Credentials Created' : 'Requested'}
                                        </span>
                                    )}
                                </div>
                                <p>Request snowflake credentials and connect your preferred BI Tools like Tableau, Looker, Power BI, and more.</p>
                                <ul className='features-list'>
                                    <li><span className='list-color'></span>Direct Snowflake Access</li>
                                    <li><span className='list-color'></span>All major BI Tools</li>
                                    <li><span className='list-color'></span>Full Customization</li>
                                </ul>
                                <Button
                                    color="primary"
                                    type="submit"
                                    className="btn btn-primary button-primary"
                                    onClick={handleRequestCredentials}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Requesting...
                                        </>
                                    ) : (
                                        'Request Snowflake Credentials'
                                    )}
                                </Button>
                            </CardBody>
                        </Card>
                    </div>
                </Container>
            </div>
            <Modal isOpen={confirmationModalOpen} toggle={toggleConfirmationModal} modalClassName={"general-custom-modal"}>
                <ModalHeader toggle={toggleConfirmationModal}>
                    {isNewRequest
                        ? 'Request Submitted Successfully'
                        : hasRequested && requestedAt
                        ? (isCreated ? 'Credentials Already Created' : 'Request Already Made')
                        : 'Snowflake Warehouse Created !'}
                </ModalHeader>
                <ModalBody>
                    <div className='confirmation-content'>
                        <span className='check-icon-circle'>
                            <i className="far fa-check-circle check-icon"></i>
                        </span>
                        {isNewRequest ? (
                            <>
                                <h5>Your Snowflake credentials are being requested</h5>
                                <p style={{ textAlign: 'center', padding: '0 20px' }}>
                                    You will receive your credentials on email at {userEmail}
                                </p>
                            </>
                        ) : hasRequested && requestedAt ? (
                            <>
                                <h5>{isCreated ? 'Credentials Already Created' : 'Request Already Submitted'}</h5>
                                <p style={{ textAlign: 'center', padding: '0 20px' }}>
                                    {getModalMessage()}
                                </p>
                            </>
                        ) : (
                            <>
                                <h5>Your Snowflake warehouse has been created!</h5>
                                <p>Credentials will be sent to your registered email.</p>
                            </>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="d-flex gap-3 justify-content-end">
                        <button className="btn btn-primary button-primary w-100 w-sm-auto" onClick={() => { setConfirmationModalOpen(false) }}>Close</button>
                    </div>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default DataOptions;
